import { useFetch } from "@/lib/useFetch";
import { Link } from "react-router-dom";
import { Check, X, Globe } from "lucide-react";
import { Spinner } from "@/app/ui";

const ORDER = ["free", "basic", "full"];
const TAGLINE = { free: "For getting started solo", basic: "For growing sole traders", full: "For teams that do it all" };

export default function Pricing() {
  const { data, loading } = useFetch("/public/pricing");
  if (loading || !data) return <div className="py-20"><Spinner /></div>;
  const cur = data.currency || "€";
  const vatNote = data.vat_inclusive ? "VAT included" : "VAT excluded";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-navy">Simple, honest pricing</h1>
        <p className="mt-4 text-lg text-slate-600">One subscription per business. Start free, upgrade anytime.</p>
        <span className="mt-3 inline-block text-xs font-semibold px-3 py-1 rounded-full bg-slate-100 text-slate-600">All prices monthly · {vatNote}</span>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-3 items-start">
        {ORDER.map((key) => {
          const p = data.plans[key];
          const feats = data.plan_features[key] || [];
          const highlight = key === "basic";
          return (
            <div key={key} data-testid={`pricing-plan-${key}`}
              className={`amden-card p-7 relative ${highlight ? "ring-2 ring-blue-600 shadow-xl lg:-mt-4 lg:mb-4" : ""}`}>
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold">Most popular</span>}
              <h3 className="font-display text-xl font-bold text-navy">{p.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{TAGLINE[key]}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="text-4xl font-extrabold text-navy font-mono">{cur}{p.price}</span>
                <span className="text-slate-500 mb-1">/month</span>
              </div>
              {key === "full" && <p className="text-xs text-slate-400 mt-1">Owner + up to {p.employee_limit} employees</p>}
              <Link to="/register" data-testid={`pricing-cta-${key}`}
                className={`mt-6 block text-center px-5 py-3 rounded-xl font-semibold transition-colors ${highlight ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-navy hover:bg-slate-800 text-white"}`}>
                Start with {p.name}
              </Link>
              <ul className="mt-7 space-y-3 text-sm">
                {feats.map((f) => <li key={f} className="flex gap-3 text-slate-700"><Check size={18} className="text-emerald-600 shrink-0" />{f}</li>)}
                {key === "free" && <li className="flex gap-3 text-slate-400"><X size={18} className="shrink-0" />Direct email sending, VAT, expenses, planner, jobs, Alfie & website</li>}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-10 amden-card p-7 max-w-3xl mx-auto flex flex-col sm:flex-row items-start gap-5">
        <span className="grid place-items-center h-12 w-12 rounded-xl bg-blue-50 text-blue-accent shrink-0"><Globe size={24} /></span>
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display text-lg font-bold text-navy">Website Add-on</h3>
            <span className="font-mono font-bold text-navy text-xl">+{cur}{data.website_addon_price}/month</span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Available with Basic or Full — not on the Free plan. A professional website (Home, About, Services, Contact), mobile-responsive, hosted with SSL. Enquiries land straight in your CRM.</p>
          <p className="text-xs text-slate-400 mt-2">e.g. Basic + website = {cur}{data.plans.basic.price + data.website_addon_price}/mo · Full + website = {cur}{data.plans.full.price + data.website_addon_price}/mo</p>
        </div>
      </div>
      <div className="mt-14 max-w-5xl mx-auto">
        <p className="text-center text-sm font-semibold uppercase tracking-wide text-slate-400 mb-5">Every plan, one simple system</p>
        <img src="/mockups/dashboard.png" alt="Amden One dashboard" loading="lazy" className="rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full object-cover" />
      </div>
      <p className="text-center text-sm text-slate-400 mt-8">Every plan starts with a quick, secure Stripe card setup — the Free plan is never charged. Businesses needing more than {data.plans.full.employee_limit} employees can contact Amden for custom pricing.</p>
    </div>
  );
}
